import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { formatDateTime } from '../../utils/dateFormat'

const SECTIONS = [
  { key: 'balance', label: 'Balance' },
  { key: 'orders', label: 'Orders' },
  { key: 'stock', label: 'Stock' },
  { key: 'reports', label: 'Reports' },
]

function statusMeta(status) {
  if (status === 'on') {
    return { label: 'On', color: 'success' }
  }
  if (status === 'off') {
    return { label: 'Off', color: 'default' }
  }
  return { label: 'Not set up', color: 'default' }
}

function DetailRow({ label, children }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      gap={2}
      sx={{ py: 0.75 }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ minWidth: 120, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1, textAlign: 'right' }}>{children}</Box>
    </Stack>
  )
}

/**
 * App Lock block inside Shop details (admin summary — no PIN hash).
 */
export default function ShopAppLockSummary({ appLock, onRegeneratePin }) {
  const status = appLock?.status ?? 'not_set_up'
  const meta = statusMeta(status)
  const showRegenerate = status !== 'not_set_up' && onRegeneratePin

  return (
    <Card
      variant="outlined"
      sx={{
        bgcolor: 'action.hover',
        borderColor: 'divider',
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          sx={{ mb: 1.5 }}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <LockOutlinedIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" fontWeight={600}>
              App Lock
            </Typography>
          </Stack>
          <Chip size="small" label={meta.label} color={meta.color} variant="outlined" />
        </Stack>

        {status === 'not_set_up' || !appLock ? (
          <Typography variant="body2" color="text.secondary">
            This shop has not configured App Lock on the POS yet.
          </Typography>
        ) : (
          <>
            {appLock.graceMinutes != null ? (
              <DetailRow label="Grace period">
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="flex-end"
                  gap={0.5}
                >
                  <ScheduleOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" fontWeight={500}>
                    {appLock.graceMinutes} min
                  </Typography>
                </Stack>
              </DetailRow>
            ) : null}

            {appLock.sections ? (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 1 }}
                >
                  Locked sections
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 1 }}>
                  {SECTIONS.map(({ key, label }) => {
                    const locked = appLock.sections[key] === true
                    return (
                      <Chip
                        key={key}
                        size="small"
                        label={label}
                        variant={locked ? 'filled' : 'outlined'}
                        color={locked ? 'primary' : 'default'}
                        sx={
                          locked
                            ? undefined
                            : {
                                opacity: 0.55,
                                borderStyle: 'dashed',
                              }
                        }
                      />
                    )
                  })}
                </Stack>
              </>
            ) : null}

            {appLock.pinUpdatedAt ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                PIN last updated {formatDateTime(appLock.pinUpdatedAt)}
              </Typography>
            ) : null}
          </>
        )}

        {showRegenerate ? (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<RestartAltIcon />}
              onClick={onRegeneratePin}
              fullWidth
            >
              Regenerate PIN
            </Button>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 1, textAlign: 'center' }}
            >
              Creates a new 4-digit PIN; shop devices update after sync.
            </Typography>
          </>
        ) : null}
      </CardContent>
    </Card>
  )
}
